import { JOKER_PRO_WOCHE } from '../config'
import { plusTage } from '../scheduler/datum'
import type { IsoDatum } from '../scheduler/typen'
import type { Serienstand, Tagesrunde } from './typen'

/** Wie weit zurueck gesucht wird. Reine Sicherung gegen Endlosschleifen. */
const MAX_RUECKBLICK_TAGE = 1000

/**
 * Kennung der Kalenderwoche: das Datum des zugehoerigen Montags.
 *
 * Einfacher und robuster als ISO-Wochennummern, die an Jahresgrenzen
 * ueberraschend zaehlen. Fuer den Zweck hier reicht "welche Woche war das",
 * und ein Montagsdatum beantwortet das eindeutig.
 */
export function wochenSchluessel(datum: IsoDatum): string {
  const teile = datum.split('-').map(Number)
  const d = new Date(teile[0] ?? 1970, (teile[1] ?? 1) - 1, teile[2] ?? 1, 12)
  const wochentagAbMontag = (d.getDay() + 6) % 7
  return plusTage(datum, -wochentagAbMontag)
}

/**
 * Die aktuelle Serie in geschafften Tagen.
 *
 * Zwei Regeln, die beide wichtig sind:
 *
 * 1. Der heutige Tag zaehlt nicht gegen sie, solange er laeuft. Eine Serie,
 *    die morgens um sieben schon gerissen ist, waere absurd.
 * 2. Pro Kalenderwoche darf ein Tag fehlen, ohne dass die Serie reisst. Bei
 *    Kindern beendet eine gerissene Serie oft die ganze Gewohnheit, und ein
 *    Krankheitstag oder ein Ausflug soll vier Wochen Arbeit nicht wegwischen.
 */
export function serieBerechnen(runden: Tagesrunde[], heute: IsoDatum): Serienstand {
  const geschafft = new Set(
    runden.filter((r) => r.geschafft && r.datum <= heute).map((r) => r.datum),
  )

  const heuteGeschafft = geschafft.has(heute)
  const jokerProWoche = new Map<string, number>()

  // Ist heute noch offen, beginnt die Zaehlung bei gestern. Der laufende Tag
  // darf die Serie weder verlaengern noch beenden.
  let tag = heuteGeschafft ? heute : plusTage(heute, -1)
  let laenge = 0

  for (let i = 0; i < MAX_RUECKBLICK_TAGE; i++) {
    if (geschafft.has(tag)) {
      laenge++
      tag = plusTage(tag, -1)
      continue
    }

    const woche = wochenSchluessel(tag)
    const verbraucht = jokerProWoche.get(woche) ?? 0
    if (verbraucht >= JOKER_PRO_WOCHE) break

    jokerProWoche.set(woche, verbraucht + 1)
    tag = plusTage(tag, -1)
  }

  return {
    laenge,
    jokerVerbraucht: (jokerProWoche.get(wochenSchluessel(heute)) ?? 0) > 0,
    heuteGeschafft,
  }
}
